<template>
  <div
    v-if="!projectId"
    class="flex flex-col items-center justify-center h-full"
  >
    <EmptyScreen
      source="tasks_no_project"
      text="No project selected!"
      description="Please select a project to view tasks."
      padding="mt-20"
      css="size-20"
    />
  </div>

  <div v-else id="project_tasks" class="flex flex-col gap-4 h-full">
    <div class="flex flex-col gap-4 flex-1 overflow-auto">
      <div
        v-if="loading"
        class="flex flex-col items-center justify-center gap-2 mt-20"
      >
        <CustomLoader :size="28" />
      </div>

      <DynamicCRMTable
        v-else-if="tasks?.length > 0"
        :records="tasks"
        :table-headers="tableHeaders"
        :columns="columns"
        :pagination="pagination"
        :actions="actions"
        :custom-row="customRow"
        :schema-object-data="schemaObjectData"
        :entity-type="'tasks'"
        :project-id="projectId"
        :sort-data="sortData"
        :frozen-columns="frozenColumns"
        :per-page-options="[10, 20, 50, 100]"
        height="calc(100vh - (480px + var(--banner-height)))"
        @update:pagination="handlePageChange"
        @column-order-changed="handleColumnOrderChanged"
        @refresh="fetchFilteredTasks"
        @property-added="handlePropertyAdded"
        @column-sort="handleColumnSort"
        @delete-property="handleDeleteProperty"
        @property-updated="handlePropertyUpdated"
        @freeze-column="handleFreezeColumn"
        @unfreeze-column="handleUnfreezeColumn"
      />

      <EmptyScreen
        v-else-if="tasks?.length === 0"
        source="tasks"
        text="No tasks yet! Create your first task to get started."
        padding="mt-20"
        css="size-20"
      />
    </div>

    <ConfirmationModal
      v-model:visible="deletePropertyModalVisible"
      title="Delete Property"
      message="Are you sure you want to delete this property?"
      :loading="deletePropertyLoading"
      @confirm="confirmDeleteProperty"
      @cancel="deletePropertyModalVisible = false"
    />
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { message } from "ant-design-vue";
import { useRouter } from "vue-router";
import store from "@/src/store/index.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import {
  applyFrozenColumnsToHeaders,
  generateTableHeadersFromSchema,
  handlePropertyAddedCommon,
  reorderTableHeadersByColumns,
  saveColumnOrder,
  transformRecordForTable,
} from "@/src/utils/dynamicCRMViewUtils.js";
import ProjectService from "@/src/api/ProjectService.js";
import ViewsServiceV2 from "@/src/api/ViewsServiceV2.js";
import CustomLoader from "@/src/components/CustomLoader.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import ConfirmationModal from "@/src/components/ConfirmationModal.vue";
import DynamicCRMTable from "@/src/views/dynamic_crm/DynamicCRMTable.vue";
import { applyViewConfiguration } from "@/src/utils/viewHelpers.js";
import { AbortControllerMap } from "@/src/utils/debouncedSearch.js";

const props = defineProps({
  projectId: {
    type: [Number, String],
    required: true,
  },
  viewId: {
    type: [Number, String],
    required: true,
  },
  externalFilterData: {
    type: Object,
    default: null,
  },
});

const router = useRouter();

const abortManager = new AbortControllerMap();

const tasks = ref([]);
const loading = ref(true);
const filterData = ref({});
const sortData = ref({});
const columns = ref([]);
const frozenColumns = ref([]);
const schemaObjectData = ref(null);
const tableHeaders = ref([]);
const deletePropertyModalVisible = ref(false);
const deletePropertyLoading = ref(false);
const propertyToDelete = ref(null);
const spaceId = ref(null);
const selectedProject = ref(null);

const users = computed(() => store.getters["users/users"] || []);

const pagination = ref({
  current: 1,
  pageSize: 20,
  total: 0,
});

const views = ref({
  active: [],
  inactive: [],
  current: {},
});

const actions = computed(() => []);

const customRow = (task) => {
  const resolvedRoute = router.resolve({
    name: "project-task-detail",
    params: {
      id: task.id,
      projectId: props.projectId,
    },
  });

  return {
    onClick: () => router.push(resolvedRoute),
    "data-href": resolvedRoute.href,
    style: { cursor: "pointer" },
  };
};

const fetchTaskSchema = async () => {
  try {
    if (!props.projectId) {
      console.error("No project selected");
      return;
    }

    const result = await store.dispatch("schema/fetchSchema", {
      slug: "tasks",
      projectId: props.projectId,
    });

    if (result.success) {
      const attributes = result?.schema?.attributes || [];

      const validAttributes = attributes.filter(
        (attr) =>
          attr.name &&
          attr.name.trim() !== "" &&
          attr.slug &&
          attr.slug.trim() !== "",
      );

      const mapAttribute = (attr) => ({
        id: attr.id,
        slug: attr.slug,
        name: attr.name,
        type: attr.type,
        datatype: attr.type,
        description: attr.description,
        required: attr.is_required,
        is_primary: attr.is_primary,
        is_required: attr.is_required,
        is_system: attr.is_system,
        is_multi_value: attr.is_multi_value || false,
        options: attr.options,
        default_value: attr.default_value,
        metadata: attr.metadata,
      });

      const nonRelationshipAttributes = validAttributes.filter(
        (attr) => String(attr.type || "").toLowerCase() !== "relationship",
      );

      schemaObjectData.value = {
        properties: nonRelationshipAttributes.map(mapAttribute),
        allProperties: validAttributes.map(mapAttribute),
      };

      tableHeaders.value = generateTableHeadersFromSchema(
        nonRelationshipAttributes,
        {
          excludeFields: ["id"],
          includeSystemFields: false,
        },
      );

      tableHeaders.value.push({
        label: "",
        key: "add_property",
        width: 1,
        align: "center",
        sortable: false,
        fixed: "right",
        fixWidth: "80px",
      });
    } else {
      console.error("Failed to fetch task schema:", result.errors);
      message.error("Failed to load task schema");
    }
  } catch (error) {
    console.error("Error fetching task schema:", error);
    message.error("Failed to load task schema");
  }
};

const fetchViewData = async () => {
  try {
    const data = await ViewsServiceV2.getViewById(props.viewId);

    if (data.success && data.view) {
      views.value = {
        active: [],
        inactive: [],
        current: data.view,
      };

      store.dispatch("views/setCurrentView", data.view);
      applyCurrentViewData(data.view);

      if (data.view?.columns && schemaObjectData.value) {
        const id_to_slug_map = {};
        (schemaObjectData.value.properties || []).forEach((prop) => {
          if (prop.id && prop.slug) {
            id_to_slug_map[prop.id] = prop.slug;
          }
        });

        columns.value = (data.view.columns || [])
          .map((col_id) => id_to_slug_map[col_id] || col_id)
          .filter((col) => col !== null && col !== undefined);
      }

      await fetchFilteredTasks();
    } else {
      console.error("Failed to fetch view data:", data.errors);
      views.value = {
        active: [],
        inactive: [],
        current: {
          id: props.viewId,
          name: "Default View",
          filters: {},
          sorts: {},
          page_size: 20,
        },
      };
      columns.value = [];
      frozenColumns.value = [];
      await fetchFilteredTasks();
    }
  } catch (error) {
    console.error("Error fetching view data:", error);
    views.value = {
      active: [],
      inactive: [],
      current: {
        id: props.viewId,
        name: "Default View",
        filters: {},
        sorts: {},
        page_size: 20,
      },
    };
    columns.value = [];
    frozenColumns.value = [];
    await fetchFilteredTasks();
  }
};

const resetTableHeadersToDefault = () => {
  const nonRelationshipAttributes = schemaObjectData.value?.properties || [];
  tableHeaders.value = generateTableHeadersFromSchema(
    nonRelationshipAttributes,
    {
      excludeFields: ["id"],
      includeSystemFields: false,
    },
  );

  tableHeaders.value.push({
    label: "",
    key: "add_property",
    width: 1,
    align: "center",
    sortable: false,
    fixed: "right",
    fixWidth: "80px",
  });

  applyFrozenColumnsToHeaders(tableHeaders, frozenColumns);
};

const applyCurrentViewData = (currentView) => {
  applyViewConfiguration(
    currentView,
    { filterData, sortData, pagination, frozenColumns, columns },
    tableHeaders,
    schemaObjectData,
    applyFrozenColumnsToHeaders,
  );
};

const fetchFilteredTasks = async () => {
  try {
    if (!props.projectId) {
      console.error("No project selected");
      loading.value = false;
      return;
    }

    const controller = abortManager.create("fetchTasks");

    loading.value = true;

    const response = await ProjectService.tasks.fetchTaskViewResults(
      props.projectId,
      views.value.current?.id || props.viewId,
      {
        page: pagination.value.current,
        page_size: pagination.value.pageSize,
      },
      { signal: controller.signal },
    );

    if (response?.success) {
      tasks.value = (response?.results || []).map((task) =>
        transformRecordForTable(task, schemaObjectData.value, users.value),
      );

      pagination.value.total = response?.total || 0;
      pagination.value.pageSize =
        response?.per_page || pagination.value.pageSize;

      sortData.value = response.sorts || {};

      columns.value = (response?.columns || []).filter(
        (col) => col !== null && col !== undefined,
      );

      if (response?.columns && response.columns.length > 0) {
        reorderTableHeadersByColumns(tableHeaders, response.columns);
      } else {
        resetTableHeadersToDefault();
      }
    } else {
      console.error("Failed to fetch tasks:", response?.errors);
      tasks.value = [];
      pagination.value.total = 0;
    }
  } catch (error) {
    if (AbortControllerMap.isAbortError(error)) {
      return;
    }
    console.error("Error fetching tasks:", error);
    tasks.value = [];
    pagination.value.total = 0;
  } finally {
    if (
      abortManager.get("fetchTasks") &&
      !AbortControllerMap.isAborted(abortManager.get("fetchTasks").signal)
    ) {
      loading.value = false;
      abortManager.cleanup("fetchTasks");
    } else if (!abortManager.get("fetchTasks")) {
      loading.value = false;
    }
  }
};

const applyFilter = async () => {
  try {
    const req_body = {
      filters: filterData.value,
      sorts: sortData.value,
      page_size: pagination.value.pageSize,
      current_page: pagination.value.current,
    };

    const data = await ViewsServiceV2.updateView(
      views.value.current?.id || 1,
      req_body,
    );

    if (data.success) {
      filterData.value = data.filters || {};
      if (pagination.value.selection) pagination.value.current = 1;
      await fetchFilteredTasks();
    } else {
      console.error("Failed to apply filter:", data.errors);
    }
  } catch (error) {
    console.error("Error applying filter:", error);
  }
};

const saveFrozenColumns = async () => {
  try {
    if (!views.value.current?.id) {
      console.warn("No current view ID found for saving frozen columns");
      message.warning("Please select or create a view to freeze columns");
      return { success: false };
    }

    await ViewsServiceV2.updateView(views.value.current.id, {
      frozen_columns: frozenColumns.value,
    });

    message.success("Column freeze settings saved");
    return { success: true };
  } catch (error) {
    console.error("Failed to save frozen columns:", error);
    message.error("Failed to save frozen columns");
    return { success: false, error: error.message };
  }
};

const handlePageChange = (newPagination) => {
  pagination.value = newPagination;
  loading.value = true;
  applyFilter();
};

const handleColumnOrderChanged = async (data) => {
  if (!data.columnOrder) return;

  const result = await saveColumnOrder(
    views.value.current?.id,
    data.columnOrder,
    schemaObjectData.value,
    "v2",
  );

  if (result.success) {
    columns.value = data.columnOrder;
    reorderTableHeadersByColumns(tableHeaders, data.columnOrder);
    applyFrozenColumnsToHeaders(tableHeaders, frozenColumns);
  } else {
    message.error("Failed to save column order");
  }
};

const handlePropertyAdded = async ({ property, shouldRefresh }) => {
  if (!shouldRefresh) return;

  if (property && property.id && property.slug) {
    await handlePropertyAddedCommon({
      property,
      schemaObjectData,
      tableHeaders,
      records: tasks,
      columns,
      views,
      viewFilterRef: null,
      showSuccessMessage: false,
    });
  } else {
    await fetchTaskSchema();
  }

  await Promise.resolve();
  setTimeout(() => {
    const updatedTableBody = document.querySelector(".ant-table-body");
    if (updatedTableBody) {
      const maxLeft = Math.max(
        0,
        (updatedTableBody.scrollWidth || 0) -
          (updatedTableBody.clientWidth || 0),
      );
      if (typeof updatedTableBody.scrollTo === "function") {
        updatedTableBody.scrollTo({ left: maxLeft, behavior: "smooth" });
      } else {
        updatedTableBody.scrollLeft = maxLeft;
      }
    }
  }, 0);
};

const handleColumnSort = ({ column, direction }) => {
  let currentSorts = { ...sortData.value };

  const existingSort = Object.values(currentSorts).find(
    (sort) => sort.field === column.key && sort.order === direction,
  );

  if (existingSort) {
    Object.keys(currentSorts).forEach((key) => {
      if (currentSorts[key].field === column.key) {
        delete currentSorts[key];
      }
    });

    const sortArray = Object.values(currentSorts);
    const newSortData = {};
    sortArray.forEach((sort, index) => {
      newSortData[index + 1] = sort;
    });

    sortData.value = newSortData;
    pagination.value.current = 1;
    applyFilter();
    return;
  }

  Object.keys(currentSorts).forEach((key) => {
    if (currentSorts[key].field === column.key) {
      delete currentSorts[key];
    }
  });

  const newSort = {
    field: column.key,
    order: direction === "asc" ? "asc" : "desc",
  };

  const sortArray = Object.values(currentSorts);
  sortArray.push(newSort);

  const newSortData = {};
  sortArray.forEach((sort, index) => {
    newSortData[index + 1] = sort;
  });

  sortData.value = newSortData;
  pagination.value.current = 1;
  applyFilter();
};

const handleDeleteProperty = ({ column, propertyMeta }) => {
  propertyToDelete.value = {
    column,
    propertyMeta,
  };
  deletePropertyModalVisible.value = true;
};

const confirmDeleteProperty = async () => {
  try {
    if (!propertyToDelete.value?.propertyMeta?.slug) return;

    if (!props.projectId) {
      message.error("No project selected");
      return;
    }

    deletePropertyLoading.value = true;

    const res = await ProjectService.tasks.deleteTaskSchemaAttribute(
      props.projectId,
      propertyToDelete.value.propertyMeta.slug,
    );

    if (!res || res.success !== false) {
      message.success("Property deleted successfully");

      const deletedSlug = propertyToDelete.value.propertyMeta.slug;

      if (frozenColumns.value.includes(deletedSlug)) {
        frozenColumns.value = frozenColumns.value.filter(
          (col) => col !== deletedSlug,
        );
        await saveFrozenColumns();
      }

      columns.value = columns.value.filter((col) => col !== deletedSlug);

      schemaObjectData.value.properties =
        schemaObjectData.value.properties.filter(
          (prop) => prop.slug !== deletedSlug,
        );

      tableHeaders.value = tableHeaders.value.filter(
        (header) => header.key !== deletedSlug,
      );

      await fetchFilteredTasks();
    } else {
      message.error(res.error || "Failed to delete property");
    }
  } catch (e) {
    console.error("Failed to delete property:", e);
    message.error("Failed to delete property");
  } finally {
    deletePropertyLoading.value = false;
    deletePropertyModalVisible.value = false;
    propertyToDelete.value = null;
  }
};

const handlePropertyUpdated = async ({
  column,
  propertyMeta,
  newName,
  response,
  isOptionChange = false,
}) => {
  try {
    await fetchTaskSchema();

    const isPropertyNameChange = newName !== propertyMeta?.name;

    if (isPropertyNameChange) {
      await fetchFilteredTasks();
    }
  } catch (error) {
    console.error("Error refreshing data after property update:", error);
  }
};

const handleFreezeColumn = async ({ column }) => {
  const columnIndex = columns.value.findIndex((col) => col === column.key);
  if (columnIndex === -1) {
    return;
  }

  const previousFrozenColumns = [...frozenColumns.value];
  frozenColumns.value = columns.value.slice(0, columnIndex + 1);

  const result = await saveFrozenColumns();

  if (result.success) {
    applyFrozenColumnsToHeaders(tableHeaders, frozenColumns);
  } else {
    frozenColumns.value = previousFrozenColumns;
  }
};

const handleUnfreezeColumn = async ({ column }) => {
  const previousFrozenColumns = [...frozenColumns.value];
  frozenColumns.value = [];

  const result = await saveFrozenColumns();

  if (result.success) {
    applyFrozenColumnsToHeaders(tableHeaders, frozenColumns);
  } else {
    frozenColumns.value = previousFrozenColumns;
  }
};

// Fetch project details to get space_id
const fetchProjectDetails = async () => {
  try {
    await store.dispatch("projects/fetchProjects");
    const projects = store.getters["projects/projects"] || [];
    const project = projects.find((p) => p.id === Number(props.projectId));
    if (project) {
      spaceId.value = project.space_id;
      selectedProject.value = project;
    }
  } catch (error) {
    console.error("Error fetching project details:", error);
  }
};

watch(
  () => props.projectId,
  async (newProjectId, oldProjectId) => {
    if (newProjectId && newProjectId !== oldProjectId) {
      loading.value = true;
      try {
        await fetchProjectDetails();
        await fetchTaskSchema();
        await fetchViewData();
      } catch (error) {
        console.error("Error loading tasks after project change:", error);
      } finally {
        loading.value = false;
      }
    }
  },
);

watch(
  () => props.externalFilterData,
  (newExternalFilters) => {
    if (newExternalFilters && Object.keys(newExternalFilters).length > 0) {
      filterData.value = { ...filterData.value, ...newExternalFilters };
      if (views.value.current?.id) {
        applyFilter();
      }
    } else {
      if (filterData.value.assignees) {
        delete filterData.value.assignees;
        if (views.value.current?.id) {
          applyFilter();
        }
      }
    }
  },
  { deep: true, immediate: true },
);

onMounted(async () => {
  if (!props.projectId) {
    loading.value = false;
    return;
  }

  loading.value = true;

  try {
    await fetchProjectDetails();
    await fetchTaskSchema();
    await fetchViewData();
  } catch (error) {
    console.error("Error loading tasks page:", error);
    loading.value = false;
  }
});
</script>
